import axios from 'axios';
// Keep in sync with the `signeeAppUrls`/`cloudAppUrls` env lists in
// econsent-enviroment's `configuration/env/configs/ec__app_config.json`
// (repo: product/web/econsent-enviroment) — that file is the source of truth
// for which Cortex envs exist; an env added there but not here will 404 this
// route, and vice versa the invite link Cortex builds would point at an env
// this allowlist doesn't recognize.
const CORTEX_URL_BY_ENV = {
    'api.local.medable.com': 'https://api.local.medable.com',
    'api-int-dev.medable.com': 'https://api-int-dev.medable.com',
    'api.qa.medable.com': 'https://api.qa.medable.com',
    'api.test.medable.com': 'https://api.test.medable.com',
    'api.dev.medable.com': 'https://api.dev.medable.com',
    'api.medable.com': 'https://api.medable.com',
    'api.dev.medable.cn': 'https://api.dev.medable.cn',
    'api.medable.cn': 'https://api.medable.cn',
    'api-eu1-dev.medable.com': 'https://api-eu1-dev.medable.com',
    'api-eu1.medable.com': 'https://api-eu1.medable.com',
};
const ORG_CODE_PATTERN = /^[a-z0-9][a-z0-9-]{0,99}$/;
// Marks an error as our own, already-translate()'d message — as opposed to e.g. an
// AxiosError (also `instanceof Error`) whose own .message is an untranslated,
// Cortex/axios-internal string that was never meant to reach the signer directly.
export class ConsentInviteError extends Error {
}
export const resolveCortexUrl = (environment) => CORTEX_URL_BY_ENV[environment] || null;
export const isValidOrgCode = (orgCode) => ORG_CODE_PATTERN.test(orgCode);
export const decodeConsentInvite = (token) => {
    const normalized = token.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const bytes = Uint8Array.from(atob(padded), (character) => character.charCodeAt(0));
    const parsed = JSON.parse(new TextDecoder().decode(bytes));
    const requiredFields = [
        'ec__email',
        'ec__signer_role',
        'ec__document_template',
        'ec__signed_document',
    ];
    if (requiredFields.some((field) => typeof parsed[field] !== 'string' || !parsed[field])) {
        throw new ConsentInviteError('The consent invitation is invalid.');
    }
    return parsed;
};
export const createConsentInviteApi = (cortexUrl, orgCode) => axios.create({
    baseURL: `${cortexUrl}/${orgCode}`,
    headers: {
        'Medable-Client-Key': 'c_studio_app',
    },
    withCredentials: true,
});
export const createConsentAccountApi = (cortexUrl, orgCode) => axios.create({
    baseURL: `${cortexUrl}/${orgCode}`,
    headers: {
        'Medable-Client-Key': 'MedableIAMDefaultClientKey',
    },
    withCredentials: true,
});
export const unwrapCortexResponse = (response) => {
    const body = response.data;
    if (body && typeof body === 'object' && 'data' in body) {
        return body.data;
    }
    return body;
};
// Cortex's response body (`.response.data.reason`/`.message`) can't be translated on the
// fly, so it's never shown to the signer directly — only our own errors, whose messages
// are already translate()'d at the point they're thrown, are safe to surface as-is.
export const getConsentInviteErrorMessage = (error, translate) => {
    if (error instanceof ConsentInviteError) {
        return error.message;
    }
    console.error('Consent invite request failed', error);
    return translate('The request failed.');
};
/**
 * Validates the raw URL inputs of a consent-invite route (invite token, org code,
 * environment key, and the route's environment id) and, if they check out, builds
 * the Cortex API clients and decoded invite payload needed to resolve the invite.
 * Returns `null` for any malformed/unrecognized input — the caller renders its
 * invalid-invitation state in that case.
 */
export const resolveConsentInviteContext = ({ token, orgCode, environmentKey, routeEnvironmentId, }) => {
    const cortexUrl = resolveCortexUrl(environmentKey);
    if (!token || !isValidOrgCode(orgCode) || !cortexUrl || !Number.isFinite(routeEnvironmentId)) {
        return null;
    }
    try {
        return {
            accountApi: createConsentAccountApi(cortexUrl, orgCode),
            api: createConsentInviteApi(cortexUrl, orgCode),
            cortexUrl,
            environmentKey,
            invite: decodeConsentInvite(token),
            orgCode,
            routeEnvironmentId,
            token,
        };
    }
    catch {
        return null;
    }
};
